import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LiveTag, TeamAvatar, fmtTime } from "@/components/common";
import { teamLabel } from "@/components/manage/matchUtils";
import ResultDialog from "@/components/manage/ResultDialog";
import { useAuth } from "@/context/AuthContext";
import { Users, Swords, MapPin, UserPlus, CalendarClock, ClipboardEdit, Play, AlertTriangle, Megaphone, Flag } from "lucide-react";

export default function ManageOverview({ data, refresh, teamMap, warnings, setTab }) {
  const { user } = useAuth();
  const { tournament: t, teams, matches, referees, announcements } = data;
  const [active, setActive] = useState(null);

  const live = matches.filter((m) => m.status === "live");
  const upcoming = matches.filter((m) => m.status === "pending" && m.team_a_id && m.team_b_id)
    .sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || "")).slice(0, 5);
  const recent = matches.filter((m) => m.status === "final").slice(-5).reverse();
  const names = teams.reduce((map, team) => {
    const key = (team.name || "").trim().toLowerCase();
    if (key) {
      if (!map[key]) map[key] = [];
      map[key].push(team.name.trim());
    }
    return map;
  }, {});
  const duplicateTeamNames = Object.values(names).filter((items) => items.length > 1).map((items) => items[0]);

  const start = async () => {
    try {
      await api.post(`/tournaments/${t.id}/status`, { status: "active" });
      toast.success("Tournament started");
      refresh();
    } catch (e) {
      if (e.response?.status === 402) {
        toast.message("Payment required before activation");
        const { data } = await api.post(`/tournaments/${t.id}/checkout`);
        if (data.url) window.location.href = data.url;
        return;
      }
      toast.error(e.response?.data?.detail?.message || "Could not start tournament");
    }
  };

  const stats = [
    { icon: Users, label: "Teams", value: `${teams.length}/${t.max_teams}` },
    { icon: Swords, label: "Matches", value: matches.length },
    { icon: MapPin, label: "Fields", value: (t.fields || []).length },
    { icon: Flag, label: "Referees", value: referees.length },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {stats.map((s) => (
          <Card key={s.label} className="p-4" data-testid={`stat-${s.label.toLowerCase()}`}>
            <s.icon className="h-5 w-5 text-primary" />
            <div className="mt-2 font-heading text-3xl font-black tracking-tight">{s.value}</div>
            <div className="text-label text-muted-foreground">{s.label}</div>
          </Card>
        ))}
      </div>

      {duplicateTeamNames.length > 0 && (
        <Card className="flex items-start gap-3 border-accent/50 bg-accent/5 p-4 text-accent" data-testid="duplicate-team-warning">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <div className="font-semibold">Duplicate team {duplicateTeamNames.length === 1 ? "name" : "names"} detected</div>
            <p className="text-sm">Multiple teams use: {duplicateTeamNames.join(", ")}. Rename them to prevent confusion in schedules and results.</p>
          </div>
        </Card>
      )}

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => setTab("teams")} data-testid="qa-team"><UserPlus className="mr-1.5 h-4 w-4" /> Team</Button>
        <Button variant="outline" onClick={() => setTab("referees")} data-testid="qa-referee"><Flag className="mr-1.5 h-4 w-4" /> Referee</Button>
        <Button variant="outline" onClick={() => setTab("schedule")} data-testid="qa-schedule"><CalendarClock className="mr-1.5 h-4 w-4" /> Schedule</Button>
        <Button variant="outline" onClick={() => setTab("results")} data-testid="qa-result"><ClipboardEdit className="mr-1.5 h-4 w-4" /> Enter result</Button>
        {t.status === "draft" && (
          <Button onClick={start} variant={user?.vip ? "outline" : "default"}
            className={user?.vip ? "border-purple-500 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:text-purple-800 dark:bg-purple-950/30 dark:text-purple-300" : ""}
            data-testid="qa-start">
            <Play className="mr-1.5 h-4 w-4" /> {t.paid || user?.vip ? "Start tournament" : "Pay & start tournament"}
            {user?.vip && <span className="ml-1 rounded bg-purple-600 px-1.5 py-0.5 text-[10px] font-bold text-white">VIP</span>}
          </Button>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-3">
          <SectionTitle>Current matches</SectionTitle>
          {live.length === 0 ? <Empty>No live matches right now.</Empty> :
            live.map((m) => <MatchRow key={m.id} m={m} teamMap={teamMap} onClick={() => setActive(m)} live />)}
        </div>
        <div className="space-y-3">
          <SectionTitle>Next matches</SectionTitle>
          {upcoming.length === 0 ? <Empty>No upcoming matches scheduled.</Empty> :
            upcoming.map((m) => <MatchRow key={m.id} m={m} teamMap={teamMap} onClick={() => setActive(m)} />)}
        </div>
        <div className="space-y-3">
          <SectionTitle>Recent results</SectionTitle>
          {recent.length === 0 ? <Empty>No results yet.</Empty> :
            recent.map((m) => <MatchRow key={m.id} m={m} teamMap={teamMap} final />)}
        </div>
        <div className="space-y-3">
          <SectionTitle>Schedule warnings</SectionTitle>
          {warnings.length === 0 ? <Empty>No conflicts detected.</Empty> :
            warnings.slice(0, 6).map((w, i) => (
              <div key={i} className="flex items-start gap-2 rounded-lg border border-accent/30 bg-accent/5 p-3 text-sm text-accent" data-testid="warning-item">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span>{w.message}</span>
              </div>
            ))}
        </div>
      </div>

      {announcements?.length > 0 && (
        <div className="space-y-3">
          <SectionTitle>Announcements</SectionTitle>
          {announcements.map((a) => (
            <Card key={a.id} className="flex items-start gap-3 p-4">
              <Megaphone className="mt-0.5 h-4 w-4 text-primary" />
              <div><div className="font-semibold">{a.title}</div><div className="text-sm text-muted-foreground">{a.body}</div></div>
            </Card>
          ))}
        </div>
      )}

      <ResultDialog open={!!active} onOpenChange={() => setActive(null)} match={active} teamMap={teamMap} onDone={() => { refresh(); setActive(null); }} />
    </div>
  );
}

const SectionTitle = ({ children }) => <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">{children}</h3>;
const Empty = ({ children }) => <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">{children}</p>;

function MatchRow({ m, teamMap, onClick, live, final }) {
  return (
    <Card className={`card-lift p-3 ${onClick ? "cursor-pointer" : ""}`} onClick={onClick} data-testid={`match-row-${m.id}`}>
      <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
        <span>{m.round_name}</span>
        {live ? <LiveTag /> : final ? <span className="font-semibold text-emerald-600">FINAL</span> : <span>{fmtTime(m.scheduled_time, m.timezone)}</span>}
      </div>
      <Row m={m} side="a" teamMap={teamMap} win={final && m.winner_id === m.team_a_id} />
      <Row m={m} side="b" teamMap={teamMap} win={final && m.winner_id === m.team_b_id} />
    </Card>
  );
}
function Row({ m, side, teamMap, win }) {
  const id = side === "a" ? m.team_a_id : m.team_b_id;
  const score = side === "a" ? m.score_a : m.score_b;
  return (
    <div className="flex items-center justify-between py-0.5">
      <div className="flex items-center gap-2">
        {id && teamMap[id] ? <TeamAvatar team={teamMap[id]} size={22} /> : <div className="h-[22px] w-[22px] rounded bg-muted" />}
        <span className={`text-sm ${win ? "font-bold" : ""}`}>{teamLabel(m, side, teamMap)}</span>
      </div>
      {score != null && <span className="font-heading font-black">{score}</span>}
    </div>
  );
}
